/**
 * Foto Tijdens List Page
 *
 * Werklijst voor tijdens-foto's/video aan de wasstraat. De kleden zijn eerder
 * al genoteerd en ingezeept (Reinigen-stap, wasrapport) en liggen op de
 * stapel; op het moment van wassen worden hier de tijdens-beelden gemaakt.
 *
 * - Bron: de na-inspectielijst (Status__c IN ('Drogen', 'In Nabehandeling',
 *   'Randen Plakken', 'In Reparatie')) PLUS de reinigen-lijst (Status__c =
 *   'Klaar voor Reinigen' — let op: het TS-commentaar bij getReinigenItems
 *   zegt 'Drogen' maar het Apex-filter is 'Klaar voor Reinigen'). Samen dekt
 *   dat het hele tijdens-venster: van genoteerd tot en met de nabehandeling,
 *   zodat er op elk tussenmoment geschoten kan worden.
 * - "Nog te doen" is afgeleid, geen vinkje: een kleed telt als gedaan zodra er
 *   media met fase 'reinigen' bestaat (server-telling + lokale upload-queue).
 * - Kleden dragen een handgeschreven label met het kale WI-nummer; de
 *   zoekbalk (numeriek toetsenbord) matcht "331034" op "WI-331034".
 * - Tik op een kleed opent direct de camera (foto + video) — geen
 *   tussenliggende detailpagina, twee taps van nummer naar schieten.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { WbButton, WbChip, WbFlag, WbListRow, WbSearchField, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { CameraCapture } from '../components/CameraCapture';
import { MediaStrip } from '../components/MediaStrip';
import { salesforceClient } from '../lib/salesforce';
import { getServerMediaCounts, getQueuedMediaCounts, type MediaCounts } from '../lib/media/counts';
import {
  getCachedNaInspectieItems,
  getCachedReinigenItems,
  type CachedReinigenItem,
} from '../lib/db/database';
import type { NaInspectieItem, ReinigenItem } from '../lib/salesforce/types';
import { matchtZoek } from '../lib/utils/zoek';

// Polling interval for real-time updates (60 seconds), zelfde als de zusterlijsten
const POLLING_INTERVAL_MS = 60000;

// Fase waaronder tijdens-media geregistreerd wordt. Bewust 'reinigen' en geen
// nieuwe fase: de klantlink en office-app groeperen op voor/tijdens/na en de
// fase-whitelist leeft op meerdere plekken — een extra waarde daar is een
// extra driftpunt voor een onderscheid dat buiten de wasserij niets betekent.
// De bewuste uitzondering is 'social' (migratie 155): dat onderscheid betekent
// buiten de wasserij wél iets (klant ziet het niet, Instagram wel).
const TIJDENS_FASE = 'reinigen' as const;

// Na een capture-sessie kan de upload-queue nog even bezig zijn; daarna de
// tellingen opnieuw ophalen zodat de optimistische bump vervangen wordt door
// de echte stand (zelfde patroon als de nalees in MediaStrip).
const NALEES_MS = 5000;

export function FotoTijdensListPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState<NaInspectieItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Tellingen: server (media-tabel) + lokale upload-queue, samen de waarheid
  const [serverCounts, setServerCounts] = useState<MediaCounts>({});
  const [queueCounts, setQueueCounts] = useState<MediaCounts>({});

  const [cameraItem, setCameraItem] = useState<NaInspectieItem | null>(null);
  const [stripItemId, setStripItemId] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [mediaRefresh, setMediaRefresh] = useState(0);

  // Fetch counts for the given items (server + queue)
  const refreshCounts = useCallback(async (forItems: NaInspectieItem[]) => {
    const ids = forItems.map((i) => i.itemId);
    const [server, queued] = await Promise.all([
      getServerMediaCounts(ids, TIJDENS_FASE),
      getQueuedMediaCounts(TIJDENS_FASE),
    ]);
    setServerCounts(server);
    setQueueCounts(queued);
  }, []);

  // ReinigenItem ('Klaar voor Reinigen') naar de NaInspectieItem-vorm brengen
  // zodat de lijst met één itemtype werkt. Status staat niet in de DTO maar is
  // per definitie 'Klaar voor Reinigen' (het Apex-filter van dat endpoint).
  const mapReinigenItem = (item: ReinigenItem | CachedReinigenItem): NaInspectieItem => ({
    itemId: item.itemId,
    itemName: item.itemName,
    productName: item.productName,
    werkOrderId: item.werkOrderId,
    werkOrderName: item.werkOrderName,
    status: 'Klaar voor Reinigen',
    companyCamProjectId: item.companyCamProjectId,
    fotosUrl: null,
    lengthCm: item.lengthCm,
    widthCm: item.widthCm,
    diameterCm: item.diameterCm,
    surfaceM2: item.surfaceM2,
    vorm: item.vorm,
    voorinspectie: item.voorinspectie,
    voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
    wasrapport: item.wasrapport,
    secondaryItems: item.secondaryItems.map((s) => ({ ...s, productCode: '' })),
    isKlacht: item.isKlacht,
    klachtReden: item.klachtReden,
    reparatieNummer: null,
    reparatieStatus: null,
    reparatieOmschrijving: null,
  });

  // Twee bronnen samenvoegen; dedupe op itemId (statussen overlappen niet,
  // maar een kleed dat tussen de twee calls in van status wisselt wel).
  const mergeItems = (
    klaarVoorReinigen: NaInspectieItem[],
    naInspectie: NaInspectieItem[]
  ): NaInspectieItem[] => {
    const seen = new Set<string>();
    const merged: NaInspectieItem[] = [];
    for (const item of [...klaarVoorReinigen, ...naInspectie]) {
      if (seen.has(item.itemId)) continue;
      seen.add(item.itemId);
      merged.push(item);
    }
    return merged;
  };

  // Fetch items: reinigen-lijst ('Klaar voor Reinigen') + na-inspectielijst
  // ('Drogen' t/m 'In Reparatie'), met de caches van beide als fallback
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const [reinigenRes, naInspectieRes] = await Promise.allSettled([
        salesforceClient.getReinigenItems(),
        salesforceClient.getNaInspectieItems(),
      ]);

      const reinigenOk =
        reinigenRes.status === 'fulfilled' && reinigenRes.value.success && reinigenRes.value.data;
      const naInspectieOk =
        naInspectieRes.status === 'fulfilled' &&
        naInspectieRes.value.success &&
        naInspectieRes.value.data;

      // Beide bronnen nodig: met een halve lijst lijkt een deel van de kleden
      // te ontbreken en dat is hier erger dan een foutmelding.
      if (!reinigenOk || !naInspectieOk) {
        throw new Error('Kon items niet ophalen');
      }

      const merged = mergeItems(
        (reinigenRes.value.data!.items || []).map(mapReinigenItem),
        naInspectieRes.value.data!.items || []
      );
      setItems(merged);
      void refreshCounts(merged);
    } catch (err) {
      console.error('[FotoTijdensListPage] Error fetching items:', err);

      // Cache-fallback: de Reinigen- en na-inspectielijsten cachen dezelfde
      // items in IndexedDB
      try {
        const [cachedReinigen, cachedNaInspectie] = await Promise.all([
          getCachedReinigenItems(),
          getCachedNaInspectieItems(),
        ]);
        const mappedNaInspectie: NaInspectieItem[] = cachedNaInspectie.map((item) => ({
          itemId: item.itemId,
          itemName: item.itemName,
          productName: item.productName,
          werkOrderId: item.werkOrderId,
          werkOrderName: item.werkOrderName,
          status: item.status,
          companyCamProjectId: item.companyCamProjectId,
          fotosUrl: item.fotosUrl,
          lengthCm: item.lengthCm,
          widthCm: item.widthCm,
          diameterCm: item.diameterCm,
          surfaceM2: item.surfaceM2,
          vorm: item.vorm,
          voorinspectie: item.voorinspectie,
          voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
          wasrapport: item.wasrapport,
          secondaryItems: item.secondaryItems,
          isKlacht: item.isKlacht,
          klachtReden: item.klachtReden,
          reparatieNummer: item.reparatieNummer,
          reparatieStatus: item.reparatieStatus,
          reparatieOmschrijving: item.reparatieOmschrijving,
        }));
        const merged = mergeItems(cachedReinigen.map(mapReinigenItem), mappedNaInspectie);
        if (merged.length > 0) {
          setItems(merged);
          setError('Offline modus - gegevens kunnen verouderd zijn');
          void refreshCounts(merged);
        } else {
          setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
          setItems([]);
        }
      } catch {
        setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
        setItems([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [refreshCounts]);

  // Fetch items on mount
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Totale telling per item: geregistreerd + nog in de upload-queue
  const countFor = useCallback(
    (itemId: string) => (serverCounts[itemId] ?? 0) + (queueCounts[itemId] ?? 0),
    [serverCounts, queueCounts]
  );

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    return items.filter((item) => matchtZoek(searchQuery, item.itemName, item.werkOrderName, item.productName));
  }, [items, searchQuery]);

  // Splitsen in nog te doen / gedaan — afgeleid uit de media-tellingen
  const todoItems = useMemo(
    () => filteredItems.filter((item) => countFor(item.itemId) === 0),
    [filteredItems, countFor]
  );
  const doneItems = useMemo(
    () => filteredItems.filter((item) => countFor(item.itemId) > 0),
    [filteredItems, countFor]
  );

  const totalTodo = useMemo(
    () => items.filter((item) => countFor(item.itemId) === 0).length,
    [items, countFor]
  );

  // Handle refresh
  const handleRefresh = async () => {
    await fetchItems(true);
  };

  // Camera gesloten: optimistisch bijtellen, daarna naleeskans voor de queue
  const handleCameraClose = (aantalGemaakt: number) => {
    const item = cameraItem;
    setCameraItem(null);
    if (!item || aantalGemaakt === 0) return;

    setQueueCounts((prev) => ({
      ...prev,
      [item.itemId]: (prev[item.itemId] ?? 0) + aantalGemaakt,
    }));
    setMediaRefresh((k) => k + 1);
    setTimeout(() => {
      void refreshCounts(items);
      setMediaRefresh((k) => k + 1);
    }, NALEES_MS);
  };

  const maatVan = (item: NaInspectieItem) => {
    if (item.lengthCm && item.widthCm) return `${item.lengthCm} × ${item.widthCm}`;
    if (item.diameterCm) return `Ø ${item.diameterCm}`;
    if (item.surfaceM2) return `${item.surfaceM2.toFixed(1)} m²`;
    return null;
  };

  useHeader('Foto tijdens', `${totalTodo}/${items.length}`);

  // De camera-actie is de hoofdactie van de rij: knop rechts in plaats van chevron.
  const renderRij = (item: NaInspectieItem, done: boolean) => {
    const n = countFor(item.itemId);
    const open = stripItemId === item.itemId;
    return (
      <div key={item.itemId} className="space-y-1.5">
        <WbListRow
          onClick={done ? () => setStripItemId((prev) => (prev === item.itemId ? null : item.itemId)) : () => setCameraItem(item)}
          hoofd={item.itemName}
          chips={
            <>
              {item.status !== 'Drogen' && <WbChip kind="wacht">{item.status}</WbChip>}
              {item.isKlacht && <WbChip kind="klacht">Klacht</WbChip>}
              {done ? <WbChip kind="klaar">{n} {n === 1 ? 'opname' : 'opnames'}</WbChip> : <WbChip kind="wacht">Nog geen foto</WbChip>}
            </>
          }
          sub={[maatVan(item), item.werkOrderName, item.productName].filter(Boolean).join(' · ')}
          rechts={
            <WbButton variant={done ? 'secondary' : 'primary'} fullWidth={false} onClick={() => setCameraItem(item)} aria-label={`Foto maken van ${item.itemName}`}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Foto
            </WbButton>
          }
        />
        {done && open && (
          <div className="pl-3">
            <MediaStrip wiSfId={item.itemId} refreshKey={mediaRefresh} toonFasen={[TIJDENS_FASE]} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <WbSearchField value={searchQuery} onChange={setSearchQuery} placeholder="Nummer van het label (WI-nummer)" />

      {isLoading && <WbState variant="laden" titel="Items laden…" />}
      {!isLoading && error && items.length === 0 && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
      {!isLoading && error && items.length > 0 && <WbFlag kind="opmerking">{error}</WbFlag>}
      {!isLoading && !error && items.length === 0 && <WbState variant="leeg" titel="Geen kleden in de was." tekst="Er liggen nu geen kleden tussen genoteerd en nabehandeling." />}
      {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
        <WbState variant="leeg" titel={`Geen resultaten voor "${searchQuery}" in deze lijst.`}>
          <Link to={`/zoek?q=${encodeURIComponent(searchQuery)}`} className="wb-focus inline-flex min-h-tap items-center font-semibold underline underline-offset-4 text-wb-inkt">
            Zoek "{searchQuery}" in alle kleden
          </Link>
        </WbState>
      )}
      {!isLoading && !error && filteredItems.length > 0 && todoItems.length === 0 && (
        <WbState variant="leeg" titel="Alle kleden hebben tijdens-foto's." />
      )}

      {!isLoading && todoItems.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Nog te doen"
            teller={todoItems.length}
            actie={
              <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {todoItems.map((item) => renderRij(item, false))}
        </section>
      )}

      {/* Gedaan: ingeklapt zodat de werklijst leeg loopt */}
      {!isLoading && doneItems.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Gedaan"
            teller={doneItems.length}
            actie={
              <WbButton variant="link" onClick={() => setShowDone((v) => !v)}>
                {showDone ? 'Verberg' : 'Toon'}
              </WbButton>
            }
          />
          {showDone && doneItems.map((item) => renderRij(item, true))}
        </section>
      )}

      {/* Camera direct vanuit de lijst: nummer intikken, kleed aantikken, schieten */}
      {cameraItem && (
        <CameraCapture
          fase={TIJDENS_FASE}
          doel={{ wiSfId: cameraItem.itemId }}
          subtitle={`${cameraItem.itemName} · ${cameraItem.productName}`}
          captureStatus={cameraItem.status}
          onClose={handleCameraClose}
        />
      )}
    </div>
  );
}
